"use client";
import React, { useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useNavigate } from "react-router-dom";
import {
  Calendar,
  momentLocalizer,
  Event,
  EventProps,
  Formats,
  DateHeaderProps,
  Views,
} from "react-big-calendar";
import moment from "moment";
import CoWorkingNavBar from "./CoWorkingLandingNavBar";
import { fetchDataFromMicroCMS } from "../utils/microCMSApi";
import { ArrowRightCircleIcon } from "@heroicons/react/24/outline";
import Pagination from "./Pagination";
import "moment/locale/ja";
import "moment/locale/en-gb";
import "react-big-calendar/lib/css/react-big-calendar.css";

export interface EventItem {
  id: string;
  createdAt: string;
  title_en: string;
  title_ja: string;
  content_en: string;
  content_ja: string;
  organizer: string;
  eventDate: {
    start: string;
    end: string;
  };
  visibility: string[];
}

interface EventsResponse {
  contents: EventItem[];
  totalCount: number;
}

interface CoWorkingEventListProps {
  showButton?: boolean;
  addMargin?: boolean;
}

const localizer = momentLocalizer(moment);

interface MyDateHeaderProps extends DateHeaderProps {
  calendarEventList: Event[];
  t: TFunction;
}

const MyDateHeader: React.FC<MyDateHeaderProps> = ({
  label,
  date,
  calendarEventList,
  t,
}) => {
  const eventsOnThisDate = calendarEventList.filter((e) =>
    moment(e.start).isSame(date, "day")
  );

  return (
    <div className='rbc-date-header' style={{ position: "relative" }}>
      {label}
      {eventsOnThisDate.length > 1 && (
        <div
          className='rbc-date-label-count bg-gray-600 text-white text-xs px-2 py-1 rounded-md'
          style={{ position: "absolute", left: "5px", bottom: "5px" }}
        >
          {t("cw_lp_events_5", { count: eventsOnThisDate.length })}
        </div>
      )}
    </div>
  );
};

const CustomMonthEvent: React.FC<EventProps> = ({ event }) => {
  return (
    <div className='custom-month-event'>
      <span className='event-title'>{event.title}</span>
    </div>
  );
};

const CoWorkingEventList: React.FC<CoWorkingEventListProps> = ({
  showButton = false,
  addMargin = true,
}) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [upcomingEvents, setUpcomingEvents] = useState<EventItem[]>([]);
  const [calendarEvents, setCalendarEvents] = useState<EventItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);

  const itemsPerPage = 10;
  const eventFormUrl = process.env.REACT_APP_EVENT_FORM_URL;
  const eventReservationStatusUrl =
    process.env.REACT_APP_RESERVATION_STATUS_URL;

  useEffect(() => {
    moment.locale(i18n.language);
  }, [i18n.language]);

  useEffect(() => {
    // Fetch upcoming events for the list
    const fetchUpcomingEvents = async () => {
      setLoading(true);
      try {
        const isProduction = process.env.REACT_APP_ENV === "production";
        const currentDate = new Date().toISOString();
        const orders = "eventDate.start";
        const filters = `&filters=eventDate.start[greater_than]${currentDate}[or]eventDate.end[greater_than]${currentDate}${
          isProduction ? `&filters=public[equals]true` : ""
        }`;

        const endpoint = `events?orders=${orders}&limit=${itemsPerPage}&offset=${
          (currentPage - 1) * itemsPerPage
        }${filters}`;

        const data = (await fetchDataFromMicroCMS(endpoint)) as EventsResponse;
        setUpcomingEvents(data.contents);
        setTotalPages(Math.ceil(data.totalCount / itemsPerPage));
      } catch (error) {
        console.error("Error fetching upcoming events:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUpcomingEvents();
  }, [currentPage, showButton]);

  useEffect(() => {
    // Fetch all events for the calendar, including past events
    const fetchCalendarEvents = async () => {
      try {
        const endpoint = `events?orders=eventDate.start&limit=100&orders=-createdAt`;
        const data = (await fetchDataFromMicroCMS(endpoint)) as EventsResponse;
        setCalendarEvents(data.contents);
      } catch (error) {
        console.error("Error fetching calendar events:", error);
      }
    };

    fetchCalendarEvents();
  }, []);

  const handleEventClick = (eventItem: EventItem) => {
    navigate(`/events/${eventItem.id}`);
  };

  const truncateHTML = (htmlString: string, maxLength: number = 250) => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlString;
    const text = tempDiv.innerText;
    return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const currentLang = i18n.language;
  const getTitle = (item: EventItem) =>
    currentLang === "ja" ? item.title_ja : item.title_en;
  const getContent = (item: EventItem) =>
    currentLang === "ja" ? item.content_ja : item.content_en;

  const formats: Formats = {
    timeGutterFormat: "h:mm A",
    eventTimeRangeFormat: () => "",
    dayHeaderFormat: "dddd, MMMM D",
  };

  const calendarEventList: Event[] = useMemo(() => {
    return calendarEvents.map((item) => ({
      title: getTitle(item),
      start: moment(item.eventDate.start).toDate(),
      end: moment(item.eventDate.end).toDate(),
      allDay: false,
      resource: item,
    }));
  }, [calendarEvents, i18n.language]);

  const handleCalendarEventSelect = (event: Event) => {
    const eventItem = event.resource as EventItem;
    handleEventClick(eventItem);
  };

  const messages = {
    month: t("calendar_month"),
    week: t("calendar_week"),
    day: t("calendar_day"),
    today: t("calendar_today"),
    previous: t("calendar_previous"),
    next: t("calendar_next"),
    showMore: (count: number) => `+ ${count} ${t("calendar_showMore")}`,
  };

  return (
    <div className='w-full'>
      <CoWorkingNavBar />
      <div
        className={`w-full py-8 bg-[#211916] text-white ${
          addMargin ? "pt-[85px]" : "md:-mt-[3rem]"
        } px-4`}
        id='events'
      >
        <div>
          <div className='md:max-w-5xl lg:max-w-6xl mx-auto'>
            <div className='relative mb-8'>
              <h1 className='text-5xl font-bold leading-[1.1] text-center pb-8 md:pt-0'>
                {t("cw_lp_common.events")}
              </h1>
              <p className='text-md font-medium text-start'>
                <Trans
                  i18nKey='cw_lp_events_2'
                  components={[
                    <a
                      href={t("cw_lp_events_3")}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='font-bold underline'
                    />,
                  ]}
                />
              </p>
              <p className='text-md font-medium text-start'>
                <Trans
                  i18nKey='cw_lp_events_6'
                  components={[
                    <a
                      href={eventFormUrl}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='font-bold underline'
                    />,
                  ]}
                />
              </p>
              <p className='text-md font-medium text-start'>
                <Trans
                  i18nKey='cw_lp_events_7'
                  components={[
                    <a
                      href={eventReservationStatusUrl}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='font-bold underline'
                    />,
                  ]}
                />
              </p>
            </div>

            {!loading && calendarEventList.length > 0 && (
              <div className='w-full mb-8 pb-1'>
                <Calendar
                  localizer={localizer}
                  events={calendarEventList}
                  startAccessor='start'
                  endAccessor='end'
                  style={{ height: 500, width: "100%" }}
                  onSelectEvent={handleCalendarEventSelect}
                  formats={formats}
                  views={["month", "week", "day"]}
                  messages={messages}
                  step={10}
                  popup={false}
                />
              </div>
            )}
          </div>
        </div>
        <div className='w-full md:max-w-5xl lg:max-w-6xl mx-auto'>
          {showButton && (
            <h2 className='text-3xl font-semibold text-center mb-4'>
              {t("cw_lp_events_4")}
            </h2>
          )}
          {loading ? (
            <p className='text-center'>{t("cw_lp_common.loading")}</p>
          ) : upcomingEvents.length === 0 ? (
            <p className='text-center'>{t("cw_lp_events_1")}</p>
          ) : (
            <div className='flex flex-col items-center w-full'>
              {upcomingEvents
                .slice(0, showButton ? 3 : upcomingEvents.length)
                .map((item) => (
                  <div
                    key={item.id}
                    className='flex flex-row w-full cursor-pointer border-b border-gray-300 py-4'
                    onClick={() => handleEventClick(item)}
                  >
                    <div className='flex-shrink-0 w-32 text-sm text-gray-300'>
                      {new Date(item.eventDate.start).toLocaleDateString(
                        "en-US",
                        {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        }
                      )}
                    </div>
                    <div className='flex-1 min-w-0 md:ml-4'>
                      <p className='font-bold text-lg break-words'>
                        {getTitle(item)}
                      </p>
                      <div
                        className='text-gray-300 mt-1 overflow-hidden text-ellipsis'
                        style={{
                          display: "-webkit-box",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                        }}
                        dangerouslySetInnerHTML={{
                          __html: truncateHTML(getContent(item), 250),
                        }}
                      ></div>
                    </div>
                  </div>
                ))}
              {totalPages > 1 && !showButton && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPrevious={handlePreviousPage}
                  onNext={handleNextPage}
                />
              )}
              {showButton && (
                <div className='relative w-full flex sm:justify-end mb-4 sm:mb-0'>
                  <button className='sm:relative absolute left-1/2 transform -translate-x-1/2 sm:left-auto sm:transform-none px-4 py-2 rounded w-[200px] hover:text-gray-400'>
                    <a
                      href='/events'
                      className='flex items-center justify-center'
                    >
                      <span className='mr-1'>{t("cw_lp_home_7")}</span>
                      <ArrowRightCircleIcon className='w-8 h-8' />
                    </a>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CoWorkingEventList;
